// src/app/components/shared/PDFViewer/PDFViewer.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'pdfjs-dist/web/pdf_viewer.css';
import { motion } from 'framer-motion';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const PDFViewer = ({
  file,
  showAll = false,
  pageNumber = 1,
  maxPages = null,
  scale = 1.0,
  width,
  height,
  onLoadSuccess,
  className = '',
  style = {},
}) => {
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const handleLoadSuccess = pdf => {
    setNumPages(pdf.numPages);
    if (onLoadSuccess) onLoadSuccess(pdf);
  };

  const totalPages = showAll ? (maxPages ? Math.min(numPages || 0, maxPages) : numPages) : 1;

  return (
    <div
      ref={containerRef}
      className={`pdf-viewer ${className}`}
      style={{
        width: width || '100%',
        height: height || 'auto',
        overflowX: 'hidden',
        ...style,
      }}
    >
      <Document
        file={file}
        onLoadSuccess={handleLoadSuccess}
        loading={
          <div className="flex justify-center items-center h-screen">
            <motion.div
              role="status"
              aria-label="Loading"
              className="relative rounded-full w-6 h-6"
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
              style={{
                background:
                  'conic-gradient(from 0deg at 50% 50%, var(--color-bg) 0deg, var(--color-bg-primary) 360deg)',
              }}
            >
              <div className="absolute inset-1 bg rounded-full" />
            </motion.div>
          </div>
        }
      >
        {file &&
          (showAll ? (
            Array.from(new Array(totalPages || 0), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={containerWidth ? containerWidth * scale : undefined}
              />
            ))
          ) : (
            <Page pageNumber={pageNumber} width={containerWidth ? containerWidth * scale : undefined} />
          ))}
      </Document>
    </div>
  );
};

export default PDFViewer;
